import { PostgrestError } from '@supabase/supabase-js';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import type { ReactNode } from 'react';

import { Sentry } from '@/lib/sentry';

const IGNORED_POSTGREST_CODES = new Set([
  'PGRST301', // JWT expired
  'PGRST302', // JWT invalid/missing
  '23505', // unique_violation (duplicate key — user error)
]);

const NON_RETRYABLE_POSTGREST_CODES = new Set([
  ...IGNORED_POSTGREST_CODES,
  '42501', // insufficient_privilege / organisation authorization failure
  'PGRST116', // .single() returned no visible row (commonly RLS-scoped)
]);

function reportToSentry(error: unknown, context: Record<string, string>) {
  // AxiosErrors are already reported by the apiInstance interceptor in configs/api.ts
  if (error instanceof AxiosError) return;

  if (error instanceof PostgrestError) {
    if (IGNORED_POSTGREST_CODES.has(error.code)) return;
    Sentry.captureException(error, {
      tags: { ...context, postgrest_code: error.code },
      extra: { details: error.details, hint: error.hint },
    });
    return;
  }

  if (error instanceof Error) {
    Sentry.captureException(error, { tags: context });
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      reportToSentry(error, {
        source: 'query',
        query_key: JSON.stringify(query.queryKey),
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      reportToSentry(error, {
        source: 'mutation',
        mutation_key: mutation.options.mutationKey ? JSON.stringify(mutation.options.mutationKey) : 'unknown',
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: (failureCount, error) => {
        if (error instanceof PostgrestError && NON_RETRYABLE_POSTGREST_CODES.has(error.code)) return false;
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

export const QueryProvider = ({ children }: { children: ReactNode }) => (
  <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
);
